import { FC, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope } from '../core/procurementLibraryUiScope'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, getInventoryItemHeads, getVesselsByCompany } from '../core/_requests'
import { CompanyGroupAdmin, InventoryItemHead, InventoryItemSubHead, Vessel } from '../core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface EditSubHeadModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (id: number, payload: { name: string; inventoryItemHeadId: number }) => Promise<void>
  subHead: InventoryItemSubHead | null
  vesselId?: number
}

const EditSubHeadModal: FC<EditSubHeadModalProps> = ({ visible, onClose, onSubmit, subHead, vesselId }) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_SUBHEAD_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'

  const [name, setName] = useState('')
  const [inventoryItemHeadId, setInventoryItemHeadId] = useState<number | ''>('')
  const [heads, setHeads] = useState<InventoryItemHead[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHeads, setIsLoadingHeads] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  const effectiveVesselId =
    (libraryScopeAll || libraryScopeCompany) && selectedVesselId
      ? Number(selectedVesselId)
      : subHead?.vesselId ?? vesselId

  const effectiveCgaId = libraryScopeAll && selectedCompanyId ? Number(selectedCompanyId) : undefined

  const loadCompanies = useCallback(async () => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getCompanyGroupAdmins()
      setCompanies(data)
    } catch (error) {
      console.error('Error loading companies:', error)
      toast.error('Failed to load companies')
      setCompanies([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVessels = useCallback(async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getVesselsByCompany(companyUsername)
      setVessels(data || [])
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels')
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVesselsForCurrentUser = useCallback(async () => {
    const companyUsername =
      (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
    if (!companyUsername) return
    await loadVessels(companyUsername)
  }, [currentUser, loadVessels])

  const loadHeads = useCallback(async (resolvedVesselId?: number, scopedCgaId?: number) => {
    if (libraryScopeAll && !resolvedVesselId && !scopedCgaId) {
      setHeads([])
      return
    }

    setIsLoadingHeads(true)
    try {
      const data = await getInventoryItemHeads(resolvedVesselId, scopedCgaId)
      setHeads(data)
    } catch (error) {
      console.error('Error loading heads:', error)
      toast.error('Failed to load heads')
      setHeads([])
    } finally {
      setIsLoadingHeads(false)
    }
  }, [libraryScopeAll])

  useEffect(() => {
    if (visible && subHead) {
      setName(subHead.name)
      setInventoryItemHeadId(subHead.inventoryItemHeadId)

      if (libraryScopeAll) {
        setSelectedCompanyId(subHead?.cgaid?.id ?? '')
        setSelectedVesselId(subHead?.vesselId ?? vesselId ?? '')
      } else if (libraryScopeCompany) {
        setSelectedCompanyId('')
        setSelectedVesselId(subHead?.vesselId ?? vesselId ?? '')
      } else {
        setSelectedCompanyId('')
        setSelectedVesselId('')
      }
    }
  }, [visible, subHead, libraryScopeAll, libraryScopeCompany, vesselId])

  useEffect(() => {
    if (!visible || !subHead) return

    if (libraryScopeAll) {
      loadCompanies()
    } else if (libraryScopeCompany) {
      loadVesselsForCurrentUser()
    }
  }, [visible, subHead, libraryScopeAll, libraryScopeCompany, loadCompanies, loadVesselsForCurrentUser])

  useEffect(() => {
    if (!visible || !libraryScopeAll || !subHead) return

    if (!selectedCompanyId) {
      setVessels([])
      setSelectedVesselId('')
      return
    }

    const selectedCompany = companies.find((c) => c.id === Number(selectedCompanyId))
    if (selectedCompany?.username) {
      loadVessels(selectedCompany.username)
    }
  }, [visible, libraryScopeAll, subHead, selectedCompanyId, companies, loadVessels])

  useEffect(() => {
    if (!visible || !subHead) return
    loadHeads(effectiveVesselId ?? undefined, effectiveCgaId)
  }, [visible, subHead, effectiveVesselId, effectiveCgaId, loadHeads])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!subHead) return

    if (!name.trim()) {
      toast.error('Please enter a sub head name')
      return
    }

    if (!inventoryItemHeadId) {
      toast.error('Please select a head')
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(subHead.id, {
        name: name.trim(),
        inventoryItemHeadId: Number(inventoryItemHeadId)
      })
      toast.success('Sub head updated successfully!')
      handleClose()
    } catch (error: any) {
      console.error('Error updating sub head:', error)
      toast.error('Failed to update sub head')
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setName('')
    setInventoryItemHeadId('')
    setHeads([])
    setCompanies([])
    setVessels([])
    setSelectedCompanyId('')
    setSelectedVesselId('')
    onClose()
  }

  if (!visible || !subHead) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-dialog-centered' role='document'>
        <div className='modal-content bg-white'>
          <div className='modal-header border-bottom'>
            <h3 className='modal-title fw-bold text-dark'>Edit Sub Head</h3>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {libraryScopeAll && (
                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold text-muted fs-7 required'>Company</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedCompanyId}
                      onChange={(e) => {
                        const nextCompanyId = e.target.value ? Number(e.target.value) : ''
                        setSelectedCompanyId(nextCompanyId)
                        setSelectedVesselId('')
                        setHeads([])
                        setInventoryItemHeadId('')
                      }}
                      disabled={isLoadingDropdowns}
                      required
                    >
                      <option value=''>Select a company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div className='col-md-6'>
                    <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                    <SearchableSelect
                      className='form-select'
                      value={selectedVesselId}
                      onChange={(e) => {
                        const nextVesselId = e.target.value ? Number(e.target.value) : ''
                        setSelectedVesselId(nextVesselId)
                        setHeads([])
                        setInventoryItemHeadId('')
                      }}
                      disabled={isLoadingDropdowns || !selectedCompanyId}
                    >
                      <option value=''>Company level (no vessel)</option>
                      {vessels.map((vessel) => (
                        <option key={vessel.id} value={vessel.id}>
                          {vessel.fleet_name}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                </div>
              )}

              {libraryScopeCompany && (
                <div className='mb-4'>
                  <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedVesselId}
                    onChange={(e) => {
                      const nextVesselId = e.target.value ? Number(e.target.value) : ''
                      setSelectedVesselId(nextVesselId)
                      setHeads([])
                      setInventoryItemHeadId('')
                    }}
                    disabled={isLoadingDropdowns}
                  >
                    <option value=''>{isLoadingDropdowns ? 'Loading vessels...' : 'Company level (no vessel)'}</option>
                    {vessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.fleet_name}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              )}

              <div className='mb-4'>
                <label className='form-label fw-semibold text-muted fs-7 required'>Sub Head Name</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='Enter sub head name'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className='mb-4'>
                <label className='form-label fw-semibold text-muted fs-7 required'>Head</label>
                <SearchableSelect
                  className='form-select'
                  value={inventoryItemHeadId}
                  onChange={(e) => setInventoryItemHeadId(Number(e.target.value))}
                  required
                  disabled={isLoadingHeads || (libraryScopeAll && !selectedCompanyId)}
                >
                  <option value=''>
                    {isLoadingHeads
                      ? 'Loading heads...'
                      : libraryScopeAll && !selectedCompanyId
                      ? 'Select company first'
                      : 'Select a head'}
                  </option>
                  {heads.map((head) => (
                    <option key={head.id} value={head.id}>
                      {head.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>

            <div className='modal-footer border-top'>
              <button
                type='button'
                className='btn btn-light'
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='btn btn_primary'
                disabled={isLoading}
              >
                {isLoading ? 'Updating...' : 'Update Sub Head'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditSubHeadModal
